import { z } from 'zod'
import { zId, zIdNullable, zIsoDate, zJsonBlob } from '../common'
import { zArticleStatus } from '../enums'

export const zArticleListItem = z.object({
  id: zId,
  tenantId: zId,
  topicId: zIdNullable,
  title: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  coverImageUrl: z.string().nullable(),
  status: zArticleStatus,
  scheduledAt: zIsoDate.nullable(),
  generatedAt: zIsoDate.nullable(),
  publishedAt: zIsoDate.nullable(),
  advisoryScore: z.number().nullable(),
  // Coût de génération en centimes d'euro. `tokenCostCents` est le TOTAL (null
  // tant que l'article n'a jamais été généré) ; il se ventile en part
  // rédactionnelle (`textCostCents`) et part images (`imageCostCents`) telles que
  // `textCostCents + imageCostCents = tokenCostCents`. La ventilation n'existe que
  // pour les articles générés après son déploiement : optional/nullable pour
  // tolérer les anciens payloads (où seuls les deux nouveaux champs manquent).
  tokenCostCents: z.number().nullable().optional(),
  imageCostCents: z.number().nullable().optional(),
  textCostCents: z.number().nullable().optional(),
  // When non-null, this article is an autonomous REFRESH that replaces the
  // article with this id at the same public slug (the old one goes `superseded`).
  // Optional: tolerated absent on backends that don't yet expose it.
  replacesArticleId: zIdNullable.optional(),
  // Denormalized mirror of the latest webhook delivery status. Kept as a loose
  // string: the DELIVERY_STATUSES enum could not be confirmed empirically (the
  // real API only ever returned null here, and webhook endpoints could not be
  // created in the smoke env — server-side encryption-key misconfig), so we do
  // NOT reject unseen values.
  lastDeliveryStatus: z.string().nullable(),
  // Multilingual: `language` is this variant's real ISO-639-1 code (no longer
  // always the tenant default); `translationGroupId` links sibling variants;
  // `sourceArticleId` is the original this was translated from (null for the
  // original); `translationStale` marks a variant needing re-translation;
  // `translatedAt` is when it was last translated. All optional to tolerate
  // pre-multilingual payloads where the fields are simply absent.
  language: z.string().optional(),
  translationGroupId: zIdNullable.optional(),
  sourceArticleId: zIdNullable.optional(),
  translationStale: z.boolean().optional(),
  translatedAt: zIsoDate.nullable().optional(),
  // True when the article body carries the tracked affiliate CTA / money-link
  // block (fence-marker injected by Seo::AffiliateCtaInjector at generation
  // time). Surfaces per-article whether the monetization link is present.
  // Defaults to false to tolerate pre-Wave-2 payloads where the field is absent.
  hasAffiliateCta: z.boolean().default(false),
  createdAt: zIsoDate,
  updatedAt: zIsoDate,
})
export type ArticleListItem = z.infer<typeof zArticleListItem>

export const zSource = z
  .object({
    url: z.string(),
    // The real API returns year as a STRING ("2025") or null — not a number.
    // Accept both to stay robust across the Rails serializer.
    year: z.union([z.string(), z.number()]).nullable().optional(),
    title: z.string().optional(),
    snippet: z.string().optional(),
  })
  .passthrough()
export type Source = z.infer<typeof zSource>

// A single quality check row, e.g. { id, label, status: 'pass'|'fail', … }.
// `actual`/`expected` are free-form (number or string depending on the check).
export const zQualityCheck = z
  .object({
    id: z.string(),
    label: z.string(),
    status: z.string(),
    message: z.string(),
    actual: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
    expected: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
  })
  .passthrough()
export type QualityCheck = z.infer<typeof zQualityCheck>

// Most categories are arrays of check rows (seo, ai_tells, readability…).
// Some are research blobs, not rows — e.g. `ground: { key_facts, open_questions }`
// on published articles (seen 2026-08 on /articles/358). A record-of-arrays
// throws, React Query goes error, and the page shows "Chargement impossible".
export const zQualityChecks = z.record(z.unknown())
export type QualityChecks = z.infer<typeof zQualityChecks>

// Revision entries keep their jsonb snake_case (`body_mdx`) untouched.
// Some revisions are non-body events (e.g. `{ at, outbound_links_added: true }`)
// and carry no `body_mdx` snapshot, so it is optional.
export const zRevision = z
  .object({
    at: z.string().nullable(),
    body_mdx: z.string().optional(),
  })
  .passthrough()
export type Revision = z.infer<typeof zRevision>

export const zArticleDetail = zArticleListItem.extend({
  bodyMdx: z.string().nullable(),
  frontmatter: zJsonBlob.nullable(),
  model: z.string().nullable(),
  outline: z.unknown().nullable(),
  draft: z.unknown().nullable(),
  qualityChecks: zQualityChecks.nullable(),
  revisions: z.array(zRevision).nullable(),
  sources: z.array(zSource).nullable(),
  tokenCostCents: z.number().nullable(),
  error: z.string().nullable(),
})
export type ArticleDetail = z.infer<typeof zArticleDetail>

// GET /admin/articles/:id/translations returns the group's sibling variants as
// plain ArticleListItem rows (verified against prod: full list-item shape plus
// the multilingual fields already declared above) — no bespoke schema needed.
